import {
  AnyAction,
  StoreEnhancer,
  Unsubscribe,
} from 'redux';
import {
  getNextIntegeterGenerator,
  shallowEqual,
} from '../Utils';

const getNextInteger = getNextIntegeterGenerator();

export interface IRegistryEntry<
  RootState,
  // Updates to start/stop subscribing to store updates:
  StartActionName extends string,
  StopActionName extends string,

  // HashInput

  // Some inputs to the computation, such as year and computation, is taken from
  // the URL parameters instead of the redux store:
  InputFromURLRouting extends object,
  UIState extends object,

  // Type of the parts of the store that are compared between store updates:
  RelevantStorePart extends object,

  ComputationInput extends object,
  SuccessfulComputationOutputAction extends AnyAction,
> {
  startListeningActionName: StartActionName;
  stopListeningActionName: StopActionName;
  getRelevantSubStore: (rootState: RootState) => RelevantStorePart;
  getComputationInput: (rootState: RootState) => ComputationInput;
  compute: (input: {
      computationInput: ComputationInput, queueNumber: number,
      inputFromURLRouting: InputFromURLRouting, uiState: UIState,
    }) => SuccessfulComputationOutputAction;
  // Return `false` if input from URL params is invalid e.g. if `country` is not
  // selected in the product space:
  isURLRoutingInputValidForUI: (input: InputFromURLRouting) => boolean;
  getURLRoutingInputFromRootState: (rootState: RootState) => InputFromURLRouting;
  getUIStateFromRootState: (rootState: RootState) => UIState;
  hasMergeAlreadyBeenComputed: (rootState: RootState) => boolean;
}

type SubscriptionTrackerEntry = {
  isSubscribed: false,
} | {
  isSubscribed: true,
  unsubscribe: Unsubscribe,
};

// Note: the keys in `SubscriptionTracker` are the "start" action names because
// they are assumed to be globally unique among all actions:
type SubscriptionTracker = Map<string, SubscriptionTrackerEntry>;

const getEnhancer = <
  RootState,
  StartActionName extends string,
  StopActionName extends string,
  InputFromURLRouting extends object,
  UIState extends object,
  RelevantStorePart extends object,
  ComputationInput extends object,
  SuccessfulComputationOutputAction extends AnyAction
>(
  entries:
    Array<
      IRegistryEntry<
        RootState,
        StartActionName,
        StopActionName,
        InputFromURLRouting,
        UIState,
        RelevantStorePart,
        ComputationInput,
        SuccessfulComputationOutputAction
      >
    >,
): StoreEnhancer<RootState> => {
  const startActionNames = entries.map(({startListeningActionName}) => startListeningActionName);
  const stopActionNames = entries.map(({stopListeningActionName}) => stopListeningActionName);

  const trackerPairs = entries.map(({startListeningActionName}) => {
    const trackerEntry: SubscriptionTrackerEntry = {
      isSubscribed: false,
    };
    const result = [startListeningActionName, trackerEntry] as [string, SubscriptionTrackerEntry];
    return result;
  });
  const tracker: SubscriptionTracker = new Map(trackerPairs);

  return (next: any) =>
    (reducer: any) => {

      const store = next(reducer);

      const newDispatch: typeof store.dispatch = <A extends AnyAction>(action: A) => {
        if (stopActionNames.includes(action.type)) {
          const {startListeningActionName} = entries.find(
            ({stopListeningActionName}) => stopListeningActionName === action.type,
          )!;
          const trackerEntry = tracker.get(startListeningActionName)!;
          if (trackerEntry.isSubscribed === true) {
            trackerEntry.unsubscribe();
            tracker.set(startListeningActionName, {
              isSubscribed: false,
            });
          }

        }

        if (startActionNames.includes(action.type)) {
          const {
            getRelevantSubStore, startListeningActionName,
            getComputationInput, getURLRoutingInputFromRootState,
            isURLRoutingInputValidForUI, compute,
            getUIStateFromRootState, hasMergeAlreadyBeenComputed,
          } = entries.find(
            entry => entry.startListeningActionName === action.type,
          )!;

          const getSubScriber = () => {
            let currentState: RootState | undefined;
            return async () => {
              const previousState = currentState;
              currentState = store.getState();
              if (currentState !== undefined && previousState !== undefined) {
                const previousRelevantSubStore = getRelevantSubStore(previousState);
                const currentRelevantSubStore = getRelevantSubStore(currentState);
                const hasRelevantSubStoresChanged = !shallowEqual(previousRelevantSubStore, currentRelevantSubStore);
                if (hasRelevantSubStoresChanged === true) {
                  const inputFromURLRouting = getURLRoutingInputFromRootState(currentState);
                  const uiState = getUIStateFromRootState(currentState);
                  // Don't bother doing computation and dispatching updated
                  // computed data because the UI will handle that:
                  if (isURLRoutingInputValidForUI(inputFromURLRouting) &&
                      hasMergeAlreadyBeenComputed(currentState) === false) {
                    const queueNumber = getNextInteger();
                    const computationInput = getComputationInput(currentState);
                    const mergeOutput = compute(
                      {computationInput, queueNumber, inputFromURLRouting, uiState},
                    );
                    if (mergeOutput !== undefined) {
                      store.dispatch(mergeOutput);
                      (self as any).postMessage(JSON.stringify(mergeOutput));
                    }
                  }
                }
              }
            };
          };
          const unsubscribe = store.subscribe(getSubScriber());
          tracker.set(startListeningActionName, {
            isSubscribed: true,
            unsubscribe,
          });
        }
        const result = store.dispatch(action);
        return result;
      };
      return Object.assign({}, store, {
        dispatch: newDispatch,
      });
    };
};

export default getEnhancer;
